<script setup>
import { ref, computed, onBeforeUnmount } from "vue";
import { Howl, Howler } from "howler";

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  trackTitle: {
    type: String,
    required: true,
  },
  artist: {
    type: String,
    default: "",
  },
});

const player = ref(null);
const isPlaying = ref(false);

function initPlayer() {
  player.value = new Howl({
    src: props.url,
    autoplay: true,
  });
  player.value.on("play", () => {
    isPlaying.value = true;
  });
  player.value.on("end", onEnded);
  player.value.on("stop", onEnded);
}

function togglePlay() {
  if (!player.value) {
    Howler.stop();
    initPlayer();
  } else if (player.value.playing()) {
    player.value.stop();
  } else {
    Howler.stop();
    player.value.play();
  }
}

function onEnded() {
  isPlaying.value = false;
}

const ariaLabel = computed(() =>
  isPlaying.value
    ? `Stop preview van ${props.trackTitle} van ${props.artist}`
    : `Luister naar een preview van ${props.trackTitle} van ${props.artist}`
);

onBeforeUnmount(() => {
  if (player.value?.unload) {
    player.value.unload();
  }
});
</script>

<template>
  <button
    type="button"
    class="hook-player"
    :class="{ 'hook-player--playing': isPlaying }"
    :aria-pressed="isPlaying ? 'true' : 'false'"
    :aria-label="ariaLabel"
    @click.prevent="togglePlay"
  >
    <svg
      v-if="!isPlaying"
      class="hook-player__icon"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 5v14l11-7L8 5z" />
    </svg>
    <svg
      v-else
      class="hook-player__icon"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M6 6h12v12H6z" />
    </svg>
  </button>
</template>

<style lang="scss">
.hook-player {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background-color: rgb(var(--color-primary));
  color: rgb(var(--color-accent-c));
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgb(var(--color-primary-dark));
    color: rgb(var(--color-accent-c));
  }

  &__icon {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>
